import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { VoiceTextarea } from '@/components/ui/voice-textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Send } from 'lucide-react';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';

export const FEATURE_META: FeatureMeta = {
  id: 'message-counterparty',
  label: 'Message a Company',
  description: 'Send a direct message to a person at another company in your network, optionally about a listing',
  triggerType: 'dialog',
  personas: ['seller', 'buyer', 'buyer_seller'],
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipientCompanyId: string;
  recipientName: string;
  recipientCompanyName: string;
  listingId?: string | null;
  contextLabel?: string; // e.g. "Blue Dream 500g"
}

export default function MessageCounterpartyDialog({
  open,
  onOpenChange,
  recipientCompanyId,
  recipientName,
  recipientCompanyName,
  listingId,
  contextLabel,
}: Props) {
  useFeatureRegistration(FEATURE_META);
  const { user } = useAuth();
  const [message, setMessage] = useState(
    contextLabel ? `Hi ${recipientName.split(' ')[0] || ''},\n\nFollowing up on ${contextLabel}. Let me know if you'd like to talk.\n` : `Hi ${recipientName.split(' ')[0] || ''},\n\n`
  );
  const [submitting, setSubmitting] = useState(false);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !message.trim()) return;
    setSubmitting(true);
    try {
      const { data: me, error: meErr } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();
      if (meErr || !me?.company_id) {
        toast.error('Could not send', { description: 'Your account is not linked to a company.' });
        return;
      }
      if (me.company_id === recipientCompanyId) {
        toast.error('Could not send', { description: "You can't message your own company." });
        return;
      }

      const { data: inserted, error } = await supabase
        .from('platform_messages')
        .insert({
          sender_user_id: user.id,
          sender_company_id: me.company_id,
          recipient_company_id: recipientCompanyId,
          listing_id: listingId ?? null,
          content: message.trim(),
        })
        .select('id')
        .single();

      if (error || !inserted) {
        toast.error('Could not send', { description: error?.message || 'Unknown error' });
        return;
      }

      /* The email notification is dispatched server-side by the
         `trg_notify_platform_message` trigger on `platform_messages`, so it no
         longer depends on this tab staying open. */
      toast.success('Message sent', { description: `Delivered to ${recipientCompanyName}` });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Message {recipientName}</DialogTitle>
          <DialogDescription>
            {recipientCompanyName}
            {contextLabel ? ` · Re: ${contextLabel}` : ''}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSend} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="cp-msg">Your message</Label>
            <VoiceTextarea onValueChange={setMessage}
              id="cp-msg"
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="quiet" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !message.trim()}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}